import { Input, Label } from '@slal/ui/forms';
import { Controller } from '@slal/ui/react-hook-form/core';
import { Heading } from '@slal/ui/typography';
import { useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { useFunds } from 'src/context/useFunds';
import { Card, Checkbox, DocumentLink, FieldError, useAnalytics } from '~/components';
import { useInvestmentFundsCardData } from './hooks';
import { strings } from './strings';
import {
    AllocationInputGroup,
    CheckboxWrapper,
    StyledFundDetails,
    StyledParagraph,
    StyledRow,
    StyledTotalAllocation,
} from './styles';
import { ComponentProps } from './types';

export const InvestmentFundsCard = ({ setIsTotalAllocationError }: ComponentProps) => {
    const { funds, updateSelectedFunds } = useFunds();
    const [isCardFocused, setIsCardFocused] = useState(false);
    const {
        watch,
        control,
        setValue,
        totalAllocation,
        isInteracted,
        isTotalAllocationOverLimit,
        isTotalAllocationInvalid,
        investmentFundsCardDisabled,
        handleBlurValidation,
        handleFieldBlur,
    } = useInvestmentFundsCardData();
    const isTotalAllocationError =
        (isTotalAllocationInvalid && !isCardFocused && isInteracted) || isTotalAllocationOverLimit;

    useEffect(() => {
        setIsTotalAllocationError(isTotalAllocationError);
    }, [isTotalAllocationError, setIsTotalAllocationError]);

    const { pushEvent, hasFiredAnalytics } = useAnalytics();
    useEffect(() => {
        if (!investmentFundsCardDisabled && !hasFiredAnalytics) {
            pushEvent({ eventAction: 'Investment funds- displayed', eventLabel: 'Standard Life Future Advantage 1' });
            pushEvent({ eventAction: 'Investment funds- displayed', eventLabel: 'Standard Life Future Advantage 2' });
            pushEvent({ eventAction: 'Investment funds- displayed', eventLabel: 'Standard Life Future Advantage 3' });
            pushEvent({ eventAction: 'Investment funds- displayed', eventLabel: 'Standard Life Future Advantage 4' });
            pushEvent({ eventAction: 'Investment funds- displayed', eventLabel: 'Standard Life Future Advantage 5' });
        }
    }, [investmentFundsCardDisabled]);

    const handleCardBlur = () => {
        setIsCardFocused(false);
        handleBlurValidation();
    };

    return (
        <Card
            heading={strings.investmentFunds}
            disabled={investmentFundsCardDisabled}
            aria-disabled={investmentFundsCardDisabled}
            onFocus={() => setIsCardFocused(true)}
            onBlur={handleCardBlur}
        >
            <StyledRow>
                <Heading level={4}>{strings.selectFunds}</Heading>
                <Heading level={4}>{strings.allocation}</Heading>
            </StyledRow>
            {funds.map((fund, i) => {
                return (
                    <StyledRow key={`fund${i + 1}`} className={i === 4 ? 'last-row' : ''}>
                        <CheckboxWrapper>
                            <Controller
                                name={`check${i + 1}`}
                                control={control}
                                render={({ field }) => (
                                    <Checkbox
                                        {...field}
                                        checked={!!field.value}
                                        onChange={e => {
                                            field.onChange(e);
                                            const isChecked = e.target.checked;
                                            const allocation = watch(`fund${i + 1}`);
                                            updateSelectedFunds(fund, isChecked, allocation);
                                            if (!e.target.checked) {
                                                setValue(`fund${i + 1}`, '');
                                            } else {
                                                pushEvent({
                                                    eventAction: 'Investment funds- clicked',
                                                    eventLabel: `Standard Life Future Advantage ${i + 1}`,
                                                });
                                            }
                                        }}
                                        aria-label={`Select fund ${fund.name}`}
                                    />
                                )}
                            />
                            <StyledFundDetails>
                                <StyledParagraph {...{ id: fund.name }}>{fund.name}</StyledParagraph>
                                <DocumentLink
                                    onClick={() => {
                                        return pushEvent({
                                            eventAction: `Standard Life Future Advantage ${i + 1}`,
                                            eventLabel: 'Download fund fact sheet',
                                        });
                                    }}
                                    href={fund.externalLink}
                                >
                                    {fund.description}
                                </DocumentLink>
                            </StyledFundDetails>
                        </CheckboxWrapper>
                        <AllocationInputGroup>
                            <Controller
                                name={`fund${i + 1}`}
                                control={control}
                                render={({ field }) => (
                                    <NumericFormat
                                        {...field}
                                        id={`allocationFund${i + 1}`}
                                        valueIsNumericString
                                        disabled={!watch(`check${i + 1}`)}
                                        allowNegative={false}
                                        customInput={Input}
                                        onBlur={() => {
                                            handleFieldBlur(`fund${i + 1}`);
                                            const allocation = field.value;
                                            const isChecked = watch(`check${i + 1}`);
                                            updateSelectedFunds(fund, isChecked, allocation);
                                            if (!allocation) {
                                                setValue(`fund${i + 1}`, '');
                                            }
                                        }}
                                        autoComplete="off"
                                        aria-label={`Allocate percentage for ${fund.name}`}
                                        aria-invalid={isTotalAllocationOverLimit}
                                        aria-describedby={isTotalAllocationOverLimit ? `error-message` : undefined}
                                    />
                                )}
                            />
                            <Label htmlFor={`allocationFund${i + 1}`} aria-disabled={!watch(`check${i + 1}`)}>
                                {strings.percentageSymbol}
                            </Label>
                        </AllocationInputGroup>
                    </StyledRow>
                );
            })}
            {isTotalAllocationError && (
                <FieldError id="error-message" role="alert" errorMessage={strings.errorMessage} />
            )}
            <StyledTotalAllocation hasError={isTotalAllocationError}>
                <Heading level={4}>
                    {isTotalAllocationOverLimit
                        ? strings.allocationOverlimitMessage
                        : `Percentage left to allocate ${totalAllocation}${strings.percentageSymbol}`}
                </Heading>
            </StyledTotalAllocation>
        </Card>
    );
};
